import { useEffect, useId, useRef, useState } from "react";
import { motion } from "motion/react";
import { Check, Send, Loader2 } from "lucide-react";
import { useServerFn } from "@tanstack/react-start";
import { submitLead } from "@/lib/leads.functions";
import {
  LeadFieldsSchema,
  detectSpam,
  fieldErrors,
} from "@/lib/leadValidation";
import { trackCta, getVisitorSessionId, getLastPlacement } from "@/lib/ctaTracking";
import { currentVariant } from "@/lib/abTest";

type Status = "idle" | "submitting" | "ok" | "error";

const EMPTY = {
  name: "",
  email: "",
  phone: "",
  company: "",
  message: "",
};

export function QuickContactForm({
  source = "contact_quick_form",
}: {
  source?: string;
}) {
  const send = useServerFn(submitLead);
  const [status, setStatus] = useState<Status>("idle");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [form, setForm] = useState(EMPTY);
  const [hp, setHp] = useState(""); // honeypot — humans never see this
  const mountedAt = useRef<number>(0);
  const hpId = useId();

  useEffect(() => {
    mountedAt.current = Date.now();
  }, []);

  const started = useRef(false);

  const setField = (key: keyof typeof EMPTY) => (v: string) => {
    if (!started.current && v.trim().length > 0) {
      started.current = true;
      trackCta("form_start", source);
    }
    setForm((f) => ({ ...f, [key]: v }));
    setErrors((e) => (e[key] ? { ...e, [key]: "" } : e));
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);

    const parsed = LeadFieldsSchema.safeParse(form);
    if (!parsed.success) {
      setErrors(fieldErrors(parsed.error));
      setStatus("idle");
      return;
    }
    const spam = detectSpam({
      name: form.name,
      message: form.message,
      company: form.company,
    });
    if (spam) {
      setErrors({ message: spam });
      return;
    }
    setErrors({});
    setStatus("submitting");

    try {
      await send({
        data: {
          ...parsed.data,
          source,
          hp,
          elapsedMs: mountedAt.current ? Date.now() - mountedAt.current : 0,
          userAgent:
            typeof navigator !== "undefined" ? navigator.userAgent : undefined,
          sessionId: getVisitorSessionId() || undefined,
          variant: currentVariant() || undefined,
          path:
            typeof window !== "undefined" ? window.location.pathname : undefined,
          placement: getLastPlacement() || source,
        },
      });
      trackCta("form", source);
      setStatus("ok");
    } catch (err) {
      setStatus("error");
      setErrorMsg(
        err instanceof Error ? err.message : "Something went wrong.",
      );
    }
  };

  if (status === "ok") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="text-center py-10"
      >
        <div className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Check className="h-6 w-6" />
        </div>
        <h3 className="mt-6 text-2xl md:text-3xl font-semibold tracking-tight text-balance">
          Message received.
        </h3>
        <p className="mt-3 max-w-md mx-auto text-ink-soft">
          Arif's team will respond within one business day.
        </p>
      </motion.div>
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate className="space-y-5">
      <div className="grid sm:grid-cols-2 gap-4">
        <Field
          label="Name"
          required
          autoComplete="name"
          value={form.name}
          error={errors["name"]}
          onChange={setField("name")}
        />
        <Field
          label="Email"
          type="email"
          required
          autoComplete="email"
          value={form.email}
          error={errors["email"]}
          onChange={setField("email")}
        />
        <Field
          label="Phone (optional)"
          type="tel"
          autoComplete="tel"
          value={form.phone}
          error={errors["phone"]}
          onChange={setField("phone")}
        />
        <Field
          label="Company (optional)"
          autoComplete="organization"
          value={form.company}
          error={errors["company"]}
          onChange={setField("company")}
        />
      </div>
      <MessageField
        value={form.message}
        error={errors["message"]}
        onChange={setField("message")}
      />

      {/* Honeypot: hidden from humans and assistive tech, irresistible to bots. */}
      <div aria-hidden="true" className="absolute left-[-9999px] h-0 w-0 overflow-hidden">
        <label htmlFor={hpId}>Company website</label>
        <input
          id={hpId}
          type="text"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          value={hp}
          onChange={(e) => setHp(e.target.value)}
        />
      </div>

      {errorMsg && (
        <p className="text-sm text-red-500" role="alert">
          {errorMsg}
        </p>
      )}
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={status === "submitting"}
          className="inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50 disabled:pointer-events-none transition"
        >
          {status === "submitting" ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Sending…
            </>
          ) : (
            <>
              Send Message <Send className="h-4 w-4" />
            </>
          )}
        </button>
      </div>
      <p className="text-xs text-ink-soft/80">
        Shared only with Arif. No marketing, no third parties.
      </p>
    </form>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  required,
  error,
  autoComplete,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
  error?: string;
  autoComplete?: string;
}) {
  const id = useId();
  const errId = `${id}-error`;
  return (
    <div>
      <label htmlFor={id} className="text-xs font-semibold uppercase tracking-widest text-ink-soft mb-2 block">
        {label}
        {required && " *"}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errId : undefined}
        className={`w-full rounded-2xl border bg-background/60 px-5 py-3.5 text-sm focus:outline-none focus:ring-2 transition ${
          error
            ? "border-red-500 focus:ring-red-500/30"
            : "border-border focus:ring-primary/30 focus:border-primary"
        }`}
      />
      {error && (
        <p id={errId} className="mt-1.5 text-xs text-red-500">
          {error}
        </p>
      )}
    </div>
  );
}

function MessageField({
  value,
  onChange,
  error,
}: {
  value: string;
  onChange: (v: string) => void;
  error?: string;
}) {
  const id = useId();
  const errId = `${id}-error`;
  return (
    <div>
      <label htmlFor={id} className="text-xs font-semibold uppercase tracking-widest text-ink-soft mb-2 block">
        Message *
      </label>
      <textarea
        id={id}
        rows={5}
        maxLength={1000}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errId : undefined}
        placeholder="Briefly describe your project, site context or what you'd like to discuss."
        className={`w-full rounded-2xl border bg-background/60 px-5 py-4 text-sm focus:outline-none focus:ring-2 transition ${
          error
            ? "border-red-500 focus:ring-red-500/30"
            : "border-border focus:ring-primary/30 focus:border-primary"
        }`}
      />
      <div className="mt-1.5 flex items-center justify-between gap-3">
        {error ? (
          <p id={errId} className="text-xs text-red-500">
            {error}
          </p>
        ) : (
          <span />
        )}
        <span className="text-xs text-ink-soft/70">{value.length}/1000</span>
      </div>
    </div>
  );
}
